import { useState, useRef, useEffect } from "react";
import { useCart, type OrderData } from "./CartContext";
import { useNavigate } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ArrowLeft, Tag, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, insertOrder, sendOrderRequestEmail, getClientId } from "./supabase";
import { TurnstileWidget } from "./TurnstileWidget";
import { motion, AnimatePresence } from "motion/react";
import { ISO_COUNTRIES } from "../data/isoCountries";

/** Set when order submit succeeds; used so Turnstile error callback does not show toast after success (e.g. on thank-you page). */
let orderSubmitSucceededFlag = false;

/** Turnstile theme from page background (light/dark). */
function turnstileThemeFromBg(hex: string): "light" | "dark" {
  const h = hex.replace(/^#/, "");
  if (h.length !== 6 && h.length !== 8) return "light";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 0.5 ? "dark" : "light";
}

export function CheckoutPage() {
  const { items, totalPrice, clearCart, setLastOrder } = useCart();
  const { addOrder, siteConfig, promoCodes } = useAdminData();
  const { t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    address: "",
    notes: "",
    website: "", // honeypot
    delivery: "shipping" as "pickup" | "shipping",
  });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [turnstileErrorOccurred, setTurnstileErrorOccurred] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number } | null>(null);
  const [promoError, setPromoError] = useState("");
  const [promoAttempts, setPromoAttempts] = useState(0);
  const [promoLocked, setPromoLocked] = useState(false);

  const promoEnabled = siteConfig.features?.promoCodes;
  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITEKEY) as string | undefined;
  const mountedRef = useRef(true);
  const promoLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orderSubmitSucceededRef = useRef(false);
  useEffect(() => {
    orderSubmitSucceededFlag = false;
    return () => {
      mountedRef.current = false;
      if (promoLockTimerRef.current) clearTimeout(promoLockTimerRef.current);
    };
  }, []);

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
        <h1
          className="text-[#2D2A26] tracking-[0.1em] mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "32px", fontWeight: 400 }}
        >
          {t("checkout.cart_empty")}
        </h1>
        <p
          className="text-[#6F675F] mb-8"
          style={{ fontFamily: "'Jost', sans-serif", fontSize: "15px", fontWeight: 300 }}
        >
          {t("checkout.cart_empty_desc")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 px-6 py-3 border border-[#6F7F63] text-[#6F7F63] tracking-[0.2em] uppercase rounded-full hover:bg-[#6F7F63] hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#6F7F63]"
          style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
        >
          <ArrowLeft size={14} />
          {t("cart.continue")}
        </button>
      </div>
    );
  }

  const handleApplyPromo = () => {
    setPromoError("");

    if (promoLocked) {
      setPromoError("Too many attempts. Please wait 60 seconds.");
      return;
    }

    const code = promoInput.trim().toUpperCase();
    const promo = promoCodes.find(
      (p) => p.code.toUpperCase() === code && p.active
    );

    if (!promo) {
      const nextAttempts = promoAttempts + 1;
      setPromoAttempts(nextAttempts);
      if (nextAttempts >= 5) {
        setPromoLocked(true);
        setPromoError("Too many attempts. Please wait 60 seconds.");
        promoLockTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          setPromoLocked(false);
          setPromoAttempts(0);
        }, 60000);
        return;
      }
      setPromoError(t("checkout.promo_invalid"));
      return;
    }

    if (promo.expires_at && new Date(promo.expires_at) < new Date()) {
      setPromoError(t("checkout.promo_invalid"));
      return;
    }

    const discount =
      promo.discount_type === "percentage"
        ? Math.round(totalPrice * (promo.discount_value / 100))
        : Math.min(promo.discount_value, totalPrice);

    setAppliedPromo({ code: promo.code, discount });
    toast.success(t("checkout.promo_applied"));
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const finalTotal = appliedPromo ? Math.max(0, totalPrice - appliedPromo.discount) : totalPrice;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSupabaseConnected() && turnstileSiteKey && !turnstileToken) {
      toast.error("Please complete the verification check.");
      return;
    }

    if (form.delivery === "shipping") {
      if (!form.country.trim()) {
        toast.error("Please select your country for shipping.");
        return;
      }
      if (!form.address.trim()) {
        toast.error("Please enter your shipping address.");
        return;
      }
    }

    setSubmitting(true);

    const { website: _honeypot, ...formForOrder } = form;
    const orderData: OrderData = {
      ...formForOrder,
      items: [...items],
      total: finalTotal,
    };

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
    const seq = String(Math.floor(Math.random() * 9999)).padStart(4, "0");
    const orderNumber = `MO-${dateStr}-${seq}`;
    const notesWithPromo =
      form.notes + (appliedPromo ? `\n[Promo: ${appliedPromo.code}, -€${appliedPromo.discount}]` : "");

    const orderPayload = {
      customer_name: form.name,
      customer_email: form.email,
      customer_phone: form.phone || undefined,
      customer_country: form.delivery === "shipping" ? (form.country || undefined) : undefined,
      delivery: form.delivery,
      shipping_address: form.delivery === "shipping" ? form.address : undefined,
      notes: notesWithPromo || undefined,
      subtotal: finalTotal,
      promo_code: appliedPromo?.code,
      discount_amount: appliedPromo?.discount ?? 0,
      items: items.map((i) => ({
        product_id: Number(i.id),
        product_name: i.name,
        quantity: i.quantity,
        unit_price: i.price,
      })),
    };

    if (isSupabaseConnected()) {
      const { data: orderRow, error: insertErr } = await insertOrder(orderPayload);
      if (insertErr) {
        setSubmitting(false);
        toast.error("Could not save the order. Please try again or contact us directly.");
        return;
      }
      orderSubmitSucceededRef.current = true;
      orderSubmitSucceededFlag = true;
      addOrder({
        id: String(orderRow!.id),
        order_number: orderRow!.order_number ?? orderNumber,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        customer_country: form.delivery === "shipping" ? form.country : undefined,
        delivery: form.delivery,
        shipping_address: form.address,
        notes: notesWithPromo,
        subtotal: finalTotal,
        status: "pending",
        items: items.map((i) => ({
          product_id: i.id,
          product_name: i.name,
          quantity: i.quantity,
          unit_price: i.price,
        })),
        created_at: now.toISOString(),
      });
      sendOrderRequestEmail({
        order_number: orderRow!.order_number ?? orderNumber,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone || undefined,
        customer_country: form.delivery === "shipping" ? form.country || undefined : undefined,
        delivery: form.delivery,
        shipping_address: form.delivery === "shipping" ? form.address : undefined,
        notes: notesWithPromo || undefined,
        subtotal: finalTotal,
        items: orderPayload.items,
        client_id: getClientId(),
        turnstile_token: turnstileToken ?? undefined,
      }).then((result) => {
        if (!result.ok && mountedRef.current) {
          toast.warning("Order saved, but we could not send the notification email. We will still contact you.");
        }
      });
    } else {
      addOrder({
        id: `ord_${Date.now()}`,
        order_number: orderNumber,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        customer_country: form.delivery === "shipping" ? form.country : undefined,
        delivery: form.delivery,
        shipping_address: form.address,
        notes: notesWithPromo,
        subtotal: finalTotal,
        status: "pending",
        items: items.map((i) => ({
          product_id: i.id,
          product_name: i.name,
          quantity: i.quantity,
          unit_price: i.price,
        })),
        created_at: now.toISOString(),
      });
    }

    await new Promise((r) => setTimeout(r, 800));

    if (!mountedRef.current) return;
    setLastOrder(orderData);
    clearCart();
    setTurnstileToken(null);
    setTurnstileKey((k) => k + 1);
    setSubmitting(false);
    toast.success("Order request sent successfully!");
    navigate("/confirmation");
  };

  return (
    <div className="pt-28 pb-24 px-6 max-w-5xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[#6F675F] hover:text-[#2D2A26] transition-colors mb-8 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
        style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px", fontWeight: 300 }}
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <h1
        className="text-[#2D2A26] tracking-[0.1em] mb-12"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400 }}
      >
        {t("checkout.title")}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
        {/* Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-6">
          {/* Honeypot */}
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
            aria-hidden="true"
          >
            <label htmlFor="checkout-website">Website</label>
            <input
              id="checkout-website"
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>

          <p
            className="text-[#C7A76C] tracking-[0.3em] uppercase mb-2"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
          >
            {t("checkout.your_details")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <InputField
              label={t("checkout.name")}
              required
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Your full name"
              maxLength={100}
            />
            <InputField
              label={t("checkout.email")}
              type="email"
              required
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              placeholder="you@email.com"
              maxLength={254}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <InputField
              label={t("checkout.phone")}
              type="tel"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              placeholder="+32 xxx xxx xxx"
              maxLength={30}
            />
            <div>
              <label
                className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
                style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px", fontWeight: 400 }}
              >
                {t("checkout.delivery")}
              </label>
              <div className="flex gap-3">
                {(["shipping", "pickup"] as const).map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setForm({ ...form, delivery: method })}
                    className={`flex-1 py-3 rounded-lg tracking-[0.15em] uppercase transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] ${
                      form.delivery === method
                        ? "bg-[#6F7F63] text-white"
                        : "bg-white border border-[#E6E0D7] text-[#6F675F] hover:border-[#C7A76C]"
                    }`}
                    style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
                  >
                    {method === "shipping" ? t("checkout.shipping") : t("checkout.pickup")}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {form.delivery === "shipping" && (
            <div className="space-y-5">
              <p
                className="text-[#C7A76C] tracking-[0.3em] uppercase mb-2"
                style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
              >
                {t("checkout.shipping_address_label")}
              </p>
              <div>
                <label
                  className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px", fontWeight: 400 }}
                >
                  {t("checkout.country")} <span className="text-[#C7A76C]">*</span>
                </label>
                <select
                  required={form.delivery === "shipping"}
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 300 }}
                  aria-label={t("checkout.country")}
                >
                  <option value="">Select country</option>
                  {ISO_COUNTRIES.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <InputField
                label={t("checkout.address")}
                required
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                placeholder="Street, number, postal code, city"
                maxLength={500}
              />
            </div>
          )}

          <div>
            <label
              className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px", fontWeight: 400 }}
            >
              {t("checkout.notes")}
            </label>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={1000}
              className="w-full px-4 py-3 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors resize-none"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 300 }}
              placeholder="Gift wrapping, special requests..."
            />
          </div>

          {turnstileSiteKey && (
            <div className="space-y-2">
              <TurnstileWidget
                key={turnstileKey}
                siteKey={turnstileSiteKey}
                theme={turnstileThemeFromBg(theme.bg)}
                onVerify={(token) => {
                  setTurnstileToken(token);
                  setTurnstileErrorOccurred(false);
                }}
                onExpire={() => setTurnstileToken(null)}
                onError={() => {
                  if (orderSubmitSucceededRef.current || orderSubmitSucceededFlag) return;
                  setTurnstileToken(null);
                  setTurnstileErrorOccurred(true);
                  toast.error("Verification failed (e.g. network or browser). Use \"Retry verification\" below or refresh the page.");
                }}
              />
              {turnstileErrorOccurred && (
                <button
                  type="button"
                  onClick={() => {
                    setTurnstileErrorOccurred(false);
                    setTurnstileKey((k) => k + 1);
                  }}
                  className="text-sm text-[#6F7F63] hover:text-[#5a6b4f] underline focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  Retry verification
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={
              submitting ||
              (isSupabaseConnected() && turnstileSiteKey && !turnstileToken)
            }
            className="w-full py-4 bg-[#C7A76C] text-white tracking-[0.25em] uppercase rounded-lg hover:bg-[#B89650] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#C7A76C] focus:ring-offset-2"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 400 }}
          >
            {submitting ? t("checkout.sending") : t("checkout.send_order")}
          </button>

          <p
            className="text-center text-[#D8D1C7]"
            style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 300 }}
          >
            {t("checkout.no_payment")}
          </p>
        </form>

        {/* Order Summary */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl p-6 shadow-[0_8px_30px_rgba(40,32,20,0.06)] sticky top-28">
            <p
              className="text-[#C7A76C] tracking-[0.3em] uppercase mb-5"
              style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
            >
              {t("checkout.summary")}
            </p>
            <div className="space-y-4 mb-6">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-[#F2EEE8]">
                    <ImageWithFallback
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[#2D2A26] truncate"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "15px", fontWeight: 500 }}
                    >
                      {item.name}
                    </p>
                    <p
                      className="text-[#D8D1C7]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 300 }}
                    >
                      {item.quantity} x &euro;{item.price}
                    </p>
                  </div>
                  <p
                    className="text-[#2D2A26] shrink-0"
                    style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px" }}
                  >
                    &euro;{item.price * item.quantity}
                  </p>
                </div>
              ))}
            </div>

            {/* Promo Code Section */}
            {promoEnabled && (
              <div className="border-t border-[#E6E0D7] pt-4 mb-4">
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-[#6F7F63]/10 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Check size={14} className="text-[#6F7F63]" />
                      <span
                        className="text-[#6F7F63] tracking-[0.1em] uppercase"
                        style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
                      >
                        {appliedPromo.code}
                      </span>
                      <span
                        className="text-[#6F7F63]"
                        style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px", fontWeight: 300 }}
                      >
                        -&euro;{appliedPromo.discount}
                      </span>
                    </div>
                    <button
                      onClick={removePromo}
                      className="text-[#6F675F] hover:text-[#2D2A26] transition-colors focus:outline-none"
                      aria-label="Remove promo code"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setPromoOpen(!promoOpen)}
                      className="flex items-center gap-2 text-[#6F675F] hover:text-[#2D2A26] transition-colors w-full text-left focus:outline-none"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
                    >
                      <Tag size={14} />
                      {t("checkout.promo_code")}
                    </button>
                    <AnimatePresence>
                      {promoOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="flex gap-2 mt-3">
                            <input
                              type="text"
                              value={promoInput}
                              onChange={(e) => {
                                setPromoInput(e.target.value);
                                setPromoError("");
                              }}
                              placeholder={t("checkout.promo_placeholder")}
                              className="flex-1 px-3 py-2 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
                              style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px", fontWeight: 300 }}
                            />
                            <button
                              type="button"
                              onClick={handleApplyPromo}
                              className="px-4 py-2 bg-[#6F7F63] text-white rounded-lg tracking-[0.1em] uppercase hover:bg-[#5F6F53] transition-colors focus:outline-none focus:ring-2 focus:ring-[#6F7F63]"
                              style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
                            >
                              {t("checkout.apply")}
                            </button>
                          </div>
                          {promoError && (
                            <p
                              className="text-[#8B5C58] mt-2"
                              style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
                            >
                              {promoError}
                            </p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="border-t border-[#E6E0D7] pt-4">
              {appliedPromo && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-[#6F675F]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
                    >
                      Subtotal
                    </span>
                    <span
                      className="text-[#6F675F]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px" }}
                    >
                      &euro;{totalPrice}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-[#6F7F63]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
                    >
                      {t("checkout.discount")}
                    </span>
                    <span
                      className="text-[#6F7F63]"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px" }}
                    >
                      -&euro;{appliedPromo.discount}
                    </span>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between">
                <span
                  className="text-[#6F675F] tracking-[0.15em] uppercase"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "12px" }}
                >
                  {t("cart.total")}
                </span>
                <span
                  className="text-[#2D2A26]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "24px", fontWeight: 500 }}
                >
                  &euro;{finalTotal}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({
  label,
  type = "text",
  required,
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  label: string;
  type?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  maxLength?: number;
}) {
  return (
    <div>
      <label
        className="block text-[#6F675F] tracking-[0.15em] uppercase mb-2"
        style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px", fontWeight: 400 }}
      >
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        className="w-full px-4 py-3 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
        style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 300 }}
        placeholder={placeholder}
      />
    </div>
  );
}